import { useCallback, useState } from "react";

import type { UseCellContentDialogReturn } from "../types";

import { useSheetDataStoreSafe } from "@/routes/smart-table/sheet-data-store-context";

/**
 * Manages open/close state and row navigation for the Cell Content Dialog.
 *
 * - `open(rowIndex, columnId)` — opens the dialog for a specific cell
 * - `close()` — closes the dialog
 * - `navigateRow(delta)` — moves to the adjacent loaded row when a row resolver is provided
 */
interface UseCellContentDialogOptions {
  getRowCount?: () => number;
  getNextRowIndex?: (currentRowIndex: number, delta: number) => number;
}

export function useCellContentDialog({
  getRowCount,
  getNextRowIndex,
}: UseCellContentDialogOptions = {}): UseCellContentDialogReturn {
  const sheetDataStore = useSheetDataStoreSafe();

  const [isOpen, setIsOpen] = useState(false);
  const [rowIndex, setRowIndex] = useState(0);
  const [columnId, setColumnId] = useState("");
  const [shouldFocusEditorOnOpen, setShouldFocusEditorOnOpen] = useState(false);

  const open = useCallback((row: number, col: string, options: { focusEditor?: boolean } = {}) => {
    setRowIndex(row);
    setColumnId(col);
    setShouldFocusEditorOnOpen(options.focusEditor ?? true);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setShouldFocusEditorOnOpen(false);
  }, []);

  const navigateRow = useCallback(
    (delta: number) => {
      setShouldFocusEditorOnOpen(false);
      setRowIndex(prev => {
        if (getNextRowIndex) {
          return Math.max(0, getNextRowIndex(prev, delta));
        }

        const next = prev + delta;
        const rowCount = sheetDataStore?.totalRows ?? getRowCount?.() ?? 0;

        if (rowCount > 0) {
          const max = rowCount - 1;
          return Math.max(0, Math.min(next, max));
        }

        return Math.max(0, next);
      });
    },
    [getNextRowIndex, getRowCount, sheetDataStore],
  );

  return { isOpen, rowIndex, columnId, shouldFocusEditorOnOpen, open, close, navigateRow };
}
